import "./DynamicAutocompleteFilter.scss";

import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";

import useDynamicAutocompleteSuggestions from "../../../../../../../hooks/useDynamicAutocompleteSuggestions";
import useScrollActiveOptionIntoView from "../../../../../../../hooks/useScrollActiveOptionIntoView";
import LandingTypography from "../../../../../landingPage/components/LandingTypography";
import InputText from "../../../../inputs/InputText/InputText";

const formatSuggestionLabel = suggestion =>
  suggestion.name
    ? `${suggestion.ticker} - ${suggestion.name}`
    : suggestion.ticker;

const DynamicAutocompleteFilter = ({
  label,
  name,
  value,
  filterDataUrl,
  disabled,
  onSelect
}) => {
  const rootRef = useRef(null);
  const { phrase, setPhrase, suggestions, loading } =
    useDynamicAutocompleteSuggestions(filterDataUrl);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const { listRef, activeOptionRef } = useScrollActiveOptionIntoView(cursor);

  useEffect(() => {
    if (!dropdownOpen) {
      setPhrase(value || "");
    }
  }, [value, dropdownOpen, setPhrase]);

  useEffect(() => {
    setCursor(0);
  }, [suggestions]);

  useEffect(() => {
    if (!dropdownOpen) {
      return undefined;
    }

    const handleMouseDown = event => {
      if (rootRef.current?.contains(event.target)) {
        return;
      }
      setDropdownOpen(false);
      setPhrase(value || "");
    };

    document.addEventListener("mousedown", handleMouseDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [dropdownOpen, value, setPhrase]);

  const selectSuggestion = suggestion => {
    if (!suggestion?.ticker || disabled) {
      return;
    }
    onSelect(suggestion.ticker);
    setPhrase(suggestion.ticker);
    setDropdownOpen(false);
  };

  const handleKeyDown = event => {
    if (disabled) {
      return;
    }

    if (event.key === "ArrowDown" && cursor < suggestions.length - 1) {
      event.preventDefault();
      setCursor(cursor + 1);
      return;
    }

    if (event.key === "ArrowUp" && cursor > 0) {
      event.preventDefault();
      setCursor(cursor - 1);
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const suggestion = suggestions[cursor];
      if (!suggestion) {
        return;
      }
      selectSuggestion(suggestion);
    }

    if (event.key === "Escape") {
      setDropdownOpen(false);
      setPhrase(value || "");
    }
  };

  const showDropdown = dropdownOpen && phrase.trim().length > 0 && !disabled;

  return (
    <LandingTypography className="landing-typography_dark">
      <div
        className="relative w-full dynamic-autocomplete-filter"
        ref={rootRef}
      >
        <InputText
          name={name}
          label={label}
          value={phrase}
          disabled={disabled}
          autoComplete={false}
          onChange={next => {
            setPhrase(next);
            if (!dropdownOpen) {
              setDropdownOpen(true);
            }
          }}
          onFocus={() => {
            setDropdownOpen(true);
            setPhrase(value || phrase || "");
          }}
          onKeyDown={handleKeyDown}
          endChild={
            value && !disabled ? (
              <button
                type="button"
                className="dynamic-autocomplete-filter__clear cursor-pointer border-none bg-transparent"
                aria-label="Clear"
                onMouseDown={event => {
                  event.preventDefault();
                  onSelect("");
                  setPhrase("");
                  setDropdownOpen(false);
                }}
              >
                ×
              </button>
            ) : null
          }
        />
        {showDropdown ? (
          <ul
            ref={listRef}
            className="absolute left-0 right-0 z-20 list-none m-0 p-0 bg-white dynamic-autocomplete-filter__list"
            onMouseDown={event => {
              // Keep focus in the input while interacting with the list/scrollbar
              event.preventDefault();
            }}
          >
            {loading && suggestions.length === 0 ? (
              <li className="dynamic-autocomplete-filter__empty">…</li>
            ) : null}
            {!loading && suggestions.length === 0 ? (
              <li className="dynamic-autocomplete-filter__empty">—</li>
            ) : null}
            {suggestions.map((suggestion, index) => (
              <li
                key={`${suggestion.ticker}-${suggestion.market || index}`}
                ref={index === cursor ? activeOptionRef : null}
                className={classNames(
                  "text-xs cursor-pointer dynamic-autocomplete-filter__option",
                  {
                    "is-active": index === cursor
                  }
                )}
                onMouseDown={event => {
                  event.preventDefault();
                  selectSuggestion(suggestion);
                }}
                onMouseEnter={() => setCursor(index)}
              >
                {formatSuggestionLabel(suggestion)}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </LandingTypography>
  );
};

export default DynamicAutocompleteFilter;
